<script setup lang="ts">
/**
 * One field of a form (issue 1756, FM6): its label, the control, and exactly one line of help under it — a plain
 * sentence and an "e.g." example — that the control is described by (`aria-describedby`).
 *
 * The slot draws the control and is handed the two ids it needs: `controlId` for its `id`, so the label names it,
 * and `helpId` for its `aria-describedby`. The label wraps the control as well as pointing at it, so a click on the
 * words reaches the control and a screen that reads a control's label from the label around it still finds it.
 *
 * `group` is for a field that is several controls at once (a list editor, a picker): a label cannot wrap those
 * without capturing every click, so the words head a `role="group"` instead, and each control inside still sits in
 * the field, which is how a form checks that every control it holds has help.
 *
 * "One line" is a rule, not a hope: whatever the sentence or the example carries, line breaks are folded into a
 * space here, so the help never breaks onto a second line by itself. "Learn more" appears only when a docs href is
 * given, which is where the engine detail lives; the help line itself stays in plain words. Colours come from the
 * theme tokens only.
 *
 * A field a form validates (issue 1757) can hand in the control's own `id` (the one its checks focus) and the `errorId`
 * of its `ZhFieldError`, which goes in the `error` slot: under the help line, outside the label, so the error is never
 * part of the control's name and the help line does not move. The slot's `describedBy` is then the error id first and
 * the help id after it (CTO decisions 18 and 20 on issue 1756); without an `errorId` it is the help id alone, so a
 * field that uses none of this renders and describes exactly as before.
 */
import { computed, useId } from 'vue'
import { Label } from './ui/label'

/**
 * The kit `Label` wraps the control, so its system-label voice (mono, uppercase, tracked) is set back to the body
 * type on the wrapper — a control inherits its font, and a field's value must not read as a label — and is put on
 * the caption alone, where it belongs.
 */
const WRAPPER =
    'grid items-stretch gap-1 font-body text-sm leading-normal font-normal normal-case tracking-normal select-auto'
const CAPTION = 'text-foreground font-mono text-xs leading-none font-medium uppercase tracking-[var(--tracking-system)]'

const props = defineProps<{
    label: string
    /** One plain sentence: what the field is for. */
    help: string
    /** An example value, shown after "e.g.". */
    example: string
    /** The docs page that holds the engine detail, when there is one. */
    learnMoreHref?: string
    /** The field is several controls: the label heads a group rather than wrapping one control. */
    group?: boolean
    /** The id of the label's words, for a control that names itself by `aria-labelledby` (a kit Select trigger). */
    labelId?: string
    /** The control's own id, when the form needs a fixed one (its checks focus it). Otherwise one is made up. */
    id?: string
    /** The id of the field's error message, which then describes the control ahead of the help line. */
    errorId?: string
}>()

defineSlots<{
    default(props: { controlId: string; helpId: string; describedBy: string }): unknown
    /** The field's error message (a kit `ZhFieldError`), drawn under the help line. */
    error?(): unknown
}>()

const base = useId()
const controlId = computed(() => props.id ?? `${base}-control`)
const helpId = `${base}-help`
const labelId = computed(() => props.labelId ?? `${base}-label`)
const describedBy = computed(() => (props.errorId ? `${props.errorId} ${helpId}` : helpId))

function oneLine(text: string): string {
    return text.replace(/\s*[\r\n]+\s*/g, ' ').trim()
}

const helpLine = computed(() => `${oneLine(props.help)} e.g. ${oneLine(props.example)}`)

/**
 * The kit `Label` cancels every repeat mousedown inside it (Reka's Label, so a double-click does not select its own
 * words), and here the control sits inside the label. Left alone, that would stop a double- or triple-click selecting
 * a word or a line in an input or textarea. A repeat click that starts on the control goes no further than the
 * control, so the label never cancels it; a double-click on the caption still reaches the label as before.
 */
function keepTextSelection(event: MouseEvent): void {
    if (event.detail > 1) event.stopPropagation()
}
</script>

<template>
    <div data-slot="form-field" class="grid content-start gap-1 text-sm">
        <div v-if="group" class="grid gap-1">
            <span :id="labelId" data-slot="form-field-label" :class="CAPTION">{{ label }}</span>
            <div role="group" :aria-labelledby="labelId" :aria-describedby="describedBy">
                <slot :control-id="controlId" :help-id="helpId" :described-by="describedBy" />
            </div>
        </div>
        <Label v-else :for="controlId" :class="WRAPPER">
            <span :id="labelId" data-slot="form-field-label" :class="CAPTION">{{ label }}</span>
            <span data-slot="form-field-control" class="contents" @mousedown="keepTextSelection">
                <slot :control-id="controlId" :help-id="helpId" :described-by="describedBy" />
            </span>
        </Label>
        <p class="text-muted-foreground text-xs">
            <span :id="helpId" data-slot="form-field-help">{{ helpLine }}</span>
            <template v-if="learnMoreHref">
                {{ ' ' }}<a :href="learnMoreHref" class="underline" target="_blank" rel="noopener">Learn more</a>
            </template>
        </p>
        <slot name="error" />
    </div>
</template>
